'use client';

import { cn, formatUSD } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export interface AssetCard {
  name: string;
  imageUrl: string;
  symbol?: string;
  yield7d?: string;
  tvl?: string;
  platform?: 'uniswap' | 'uniswap-v3' | 'uniswap-v4' | 'flaunch' | 'aerodrome' | 'pancakeswap';
  baseTokenIcon?: string;
  tradeUrl?: string;
  pairLabel?: string;
}

interface AssetDeckProps {
  assets: AssetCard[];
  className?: string;
}

const MAX_VISIBLE = 6;

export function AssetDeck({ assets, className }: AssetDeckProps) {
  if (assets.length === 0) {
    return (
      <div className={cn('flex items-center gap-1', className)}>
        <span className="text-xs text-muted-foreground">No assets</span>
      </div>
    );
  }

  const visible = assets.slice(0, MAX_VISIBLE);
  const remaining = assets.length - MAX_VISIBLE;

  return (
    <div className={cn('flex items-center', className)}>
      <div className="flex items-center -space-x-2">
        {visible.map((asset, i) => (
          <Tooltip key={`${asset.name}-${i}`} delayDuration={150}>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  'w-9 h-9 rounded-full overflow-hidden border-2 border-background bg-muted shrink-0',
                  'transition-transform hover:scale-110 hover:z-10 cursor-pointer',
                  'ring-1 ring-border/30',
                )}
                style={{ zIndex: i + 1 }}
              >
                {asset.imageUrl ? (
                  <img src={asset.imageUrl} alt={asset.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-muted-foreground">
                    {asset.symbol?.slice(0, 2) || '?'}
                  </div>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="p-0 border-0 bg-transparent shadow-none">
              <AssetTooltipCard asset={asset} />
            </TooltipContent>
          </Tooltip>
        ))}
        {remaining > 0 && (
          <div
            className="w-9 h-9 rounded-full border-2 border-background bg-muted/80 shrink-0 flex items-center justify-center"
            style={{ zIndex: MAX_VISIBLE + 1 }}
          >
            <span className="text-[10px] font-semibold text-muted-foreground">+{remaining}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function AssetTooltipCard({ asset }: { asset: AssetCard }) {
  const yield7dNum = asset.yield7d ? Number(asset.yield7d) / 1e18 : null;
  const tvlNum = asset.tvl ? Number(asset.tvl) / 1e6 : null;

  return (
    <div className="rounded-lg border bg-card p-3 shadow-lg min-w-[180px] space-y-2">
      <div className="flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-full overflow-hidden bg-muted shrink-0 ring-1 ring-border/30">
          {asset.imageUrl ? (
            <img src={asset.imageUrl} alt={asset.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[9px] font-bold text-muted-foreground">
              {asset.symbol?.slice(0, 2)}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium truncate">{asset.name}</p>
          {asset.symbol && <p className="text-[10px] text-muted-foreground">{asset.symbol}</p>}
        </div>
      </div>
      {(yield7dNum !== null || tvlNum !== null) && (
        <div className="flex items-center gap-3 text-[10px]">
          {yield7dNum !== null && (
            <div>
              <span className="text-muted-foreground">7D Yield</span>
              <p className="text-win-green font-mono font-medium">+{yield7dNum.toFixed(3)}</p>
            </div>
          )}
          {tvlNum !== null && (
            <div>
              <span className="text-muted-foreground">TVL</span>
              <p className="font-mono font-medium">{formatUSD(tvlNum)}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
